import React from 'react';
import {
  Box, Typography, Button, TextField, IconButton, Divider,
  FormControl, List, MenuItem, Select,
} from '@mui/material';
import {
  Star as StarIcon, ArrowForward as ArrowForwardIcon,
  Lock as LockIcon, ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import FAQAccordion from '../../../FAQAccordion';
import ComparisonTable from '../../../ComparisonTable';
import TwoColumnComparisonTable from '../../../TwoColumnComparisonTable';
import FittableImage from '../../../FittableImage';
import CircularImageCropper from '../../../CircularImageCropper';
import ImageCropper from '../../../ImageCropper';
import Checklist from '../../../Checklist';
import CenteredVideoPlayer from '../../../CenteredVideoPlayer';
import ResizableButton from '../../../ResizableButton';
import InlineRowLayout from '../../../InlineRowLayout';
import ExtractedElementControls from '../../../ElementControls';
import { getPreset, getPresetFontFamilies } from '../../../../textStyle.js';
import { getListIcon } from '../../../../utils/offerListIcons';
import { ELEMENT_TYPES, LAYOUT_TYPES } from '../../../../utils/offerElementTypes';
import { LAYOUT_CONFIGS } from '../../../../utils/offerLayoutConfigs';
import { calculateVideoAspectRatio } from '../../../../utils/offerPreviewElement';

export const renderButtonElements = (element, ctx) => {
  const {
    isEditing, isEditingField, handleDoubleClick,
    isElementSelected, getSelectionStyles, ElementControls,
    editingElement, editingField, tempValue, setTempValue, textEditorRef,
    selectedElement, selectedElementContext, selectedSubElement,
    iconSelectorOpen, setIconSelectorOpen, isBrandsPage, paginationEnabled,
    currentPageIndex, pageSettings, forceRender, rightPanelOpen,
    setSelectedElement, setSelectedElementContext, setSelectedSubElement,
    setTabValue, setRightPanelOpen, setPages, setElements,
    setInsertAfterElementId, setPromptDialogTitle, setPromptDialogMessage,
    setPromptDialogValue, setPromptAction, setShowPromptDialog,
    startInlineEdit, saveInlineEdit, cancelInlineEdit, handleKeyPress,
    handleResizeStart, findAndUpdateLayout, moveElement, generateId,
    deleteElement, deleteElementFromZone, updateElement, applyHighlight,
    handleDragOver, handleDragLeave, handleDrop,
    getZoneHeightStyle, getHeightModeText, toggleZoneHeight, getHeightTooltip,
    getExpandIcon, selectedVariantKey, setSelectedVariantKey, targetZone,
    isInZone, layoutId, zoneId, elementIndex,
    renderInlineEditableElement,
  } = ctx;

  switch (element.type) {
case ELEMENT_TYPES.BUTTON:
  // Force unique key for button re-rendering - include border specifically to ensure re-render
  const buttonKey = `btn-${element.id}-${JSON.stringify(element.styles)}-${element.styles?.border || 'none'}`;

  return (
    <Box
      key={buttonKey}
      data-element-id={element.id}
      sx={{
        textAlign: 'center',
        position: 'relative',
        '&:hover .element-controls': { opacity: 1 },
        mb: 0,
        ...getSelectionStyles(element.id)
      }}
    >
      <ElementControls />
      {isEditingField('content') ? (
        <TextField
          value={tempValue}
          onChange={(e) => setTempValue(e.target.value)}
          onKeyDown={handleKeyPress}
          onBlur={saveInlineEdit}
          autoFocus
          variant="outlined"
          size="small"
          placeholder="Button text"
          sx={{ mb: 1 }}
        />
      ) : isEditingField('url') ? (
        <TextField
          value={tempValue}
          onChange={(e) => setTempValue(e.target.value)}
          onKeyDown={handleKeyPress}
          onBlur={saveInlineEdit}
          autoFocus
          variant="outlined"
          size="small"
          placeholder="Button URL"
          sx={{ mb: 1 }}
        />
      ) : (
        <Box>
          <ResizableButton
            key={`resizable-${element.id}-${JSON.stringify(element.styles)}-${element.styles?.border || 'none'}`}
            element={element}
            updateElement={updateElement}
            isInZone={isInZone}
            layoutId={layoutId}
            zoneId={zoneId}
            isEditing={isEditingField('content') || isEditingField('url')}
            disableResize={true}
          >
            <button
              ref={(buttonRef) => {
                if (buttonRef && element.id === selectedElement?.id) {
                  console.log('🔴 BUTTON REF - Element ID:', element.id);
                  console.log('🔴 BUTTON REF - Border style:', element.styles?.border);
                  console.log('🔴 BUTTON REF - All styles:', element.styles);

                  // Aggressive DOM border clearing and setting
                  const borderValue = element.styles?.border || 'none';
                  console.log('🔴 FORCING AGGRESSIVE DOM BORDER UPDATE:', borderValue);

                  // First, completely clear ALL border-related properties
                  buttonRef.style.removeProperty('border');
                  buttonRef.style.removeProperty('border-width');
                  buttonRef.style.removeProperty('border-style');
                  buttonRef.style.removeProperty('border-color');
                  buttonRef.style.removeProperty('border-top');
                  buttonRef.style.removeProperty('border-right');
                  buttonRef.style.removeProperty('border-bottom');
                  buttonRef.style.removeProperty('border-left');

                  // Force a reflow
                  void buttonRef.offsetHeight;

                  // Then set the new border
                  if (borderValue !== 'none') {
                    buttonRef.style.setProperty('border', borderValue, 'important');
                  } else {
                    buttonRef.style.setProperty('border', 'none', 'important');
                  }

                  console.log('🔴 Final computed border:', window.getComputedStyle(buttonRef).border);
                }
              }}
              style={{
                backgroundColor: element.styles?.backgroundColor || '#ff6b35',
                color: element.styles?.color || '#ffffff',
                padding: element.styles?.padding || '12px 24px',
                fontSize: element.styles?.fontSize || '16px',
                fontWeight: element.styles?.fontWeight || 400,
                borderRadius: element.styles?.borderRadius || '4px',
                width: '100%',
                height: '100%',
                cursor: element.styles?.cursor || 'pointer',
                marginBottom: element.styles?.marginBottom || '8px',
                // Border is now handled entirely by DOM manipulation in ref callback
                // Debug border application - removed red outline to avoid confusion
                // Add background to make border visible if it's there
                boxSizing: 'border-box',
                // Force DOM update with unique property
                '--border-update-id': element.styles?.border ? element.styles.border.replace(/[^a-zA-Z0-9]/g, '-') + '-' + forceRender : 'none-' + forceRender,
                textTransform: element.styles?.textTransform || 'none',
                fontFamily: element.styles?.fontFamily || 'inherit',
                fontStyle: element.styles?.fontStyle || 'normal',
                transform: element.styles?.transform || 'none',
                display: element.styles?.display || 'flex',
                alignItems: element.styles?.alignItems || 'center',
                justifyContent: element.styles?.justifyContent || 'center',
                textDecoration: element.styles?.textDecoration || 'none',
                transition: element.styles?.transition || 'all 0.2s ease',
                boxShadow: element.styles?.boxShadow || '0 2px 4px rgba(0,0,0,0.1)',
                background: element.styles?.background || element.styles?.backgroundColor || '#ff6b35',
                gap: element.styles?.gap || undefined,
                '&:hover': {
                  opacity: 0.8,
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                }
              }}
              data-text-transform={element.styles?.textTransform || 'none'}
              data-debug-text-transform={element.styles?.textTransform}
              data-debug-content={element.content}
              data-debug-styles={JSON.stringify(element.styles)}
              onMouseEnter={(e) => {
                e.target.style.opacity = '0.8';
                e.target.style.transform = 'translateY(-1px)';
                // Only apply hover shadow if no custom shadow is set
                if (!element.styles?.boxShadow) {
                  e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.opacity = '1';
                e.target.style.transform = 'translateY(0)';
                // Restore original shadow from element.styles or default
                e.target.style.boxShadow = element.styles?.boxShadow || '0 2px 4px rgba(0,0,0,0.1)';
              }}
              onClick={(e) => {
                e.stopPropagation();
                console.log('🔴 Button clicked - selecting element:', element.id);
                console.log('🔴 Before selection - selectedElement:', selectedElement?.id);
                console.log('🔴 Before selection - rightPanelOpen:', rightPanelOpen);
                setSelectedElement(element);
                setSelectedElementContext({ isInZone, layoutId, zoneId });
                setTabValue(1); // Set to Style tab
                setRightPanelOpen(true); // Ensure right panel is open
                console.log('🔴 After selection calls made');
              }}
              onDoubleClick={() => handleDoubleClick('content', element.content)}
              title="Click to select and edit styles • Double-click to edit text • Drag handles to resize"
            >
              {element.iconLeft === 'cart' && <ShoppingCartIcon sx={{ color: '#d4a000', fontSize: '2.2rem', mr: 1 }} />}
              <span style={{
                fontWeight: element.styles?.fontWeight || 400,
                fontSize: element.styles?.fontSize || '2rem',
                color: element.styles?.color || '#222',
                letterSpacing: element.styles?.fontWeight === 100 ? '0.5px' : 'normal',
                textShadow: element.styles?.fontWeight === 100 ? '0.5px 0.5px 0px rgba(0,0,0,0.1)' :
                  element.styles?.fontWeight === 900 ? '1px 1px 0px rgba(0,0,0,0.2)' : 'none'
              }}>
                {element.styles?.textTransform === 'titlecase' && element.content
                  ? element.content.toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
                  : element.content || 'Button Text'}
              </span>
              {element.iconRight === 'arrow' && <ArrowForwardIcon sx={{ color: '#222', fontSize: '2.2rem', ml: 1 }} />}
            </button>
          </ResizableButton>

        </Box>
      )}
    </Box>
  );

case ELEMENT_TYPES.BUTTON_WITH_NOTICE:
  const getVariantStyles = (variant, styleVariant = 'default') => {
    const styleVariants = {
      'urgent-sale': {
        'default': {
          backgroundColor: '#ff69b4', // pink
          noticeColor: '#101022', // black text on pink
          buttonBg: '#101022', // black button
          buttonColor: '#ffffff', // white text on black
          border: 'none',
          borderRadius: '8px'
        },
        'modern': {
          backgroundColor: '#ffffff', // white
          noticeColor: '#101022', // black text on white (changed from orange/yellow)
          buttonBg: '#ff69b4', // pink button
          buttonColor: '#101022', // black text on pink
          border: '2px solid #ff69b4',
          borderRadius: '12px'
        },
        'minimal': {
          backgroundColor: '#fafafa', // light gray (close to white)
          noticeColor: '#101022', // black text (changed from orange/yellow)
          buttonBg: '#ff69b4', // pink button
          buttonColor: '#101022', // black text on pink
          border: '1px solid #e0e0e0',
          borderRadius: '4px'
        },
        'gradient': {
          backgroundColor: 'linear-gradient(135deg, #ff69b4, #ff1493)', // pink gradient
          noticeColor: '#ffffff', // white text on pink gradient
          buttonBg: '#ffffff', // white button
          buttonColor: '#101022', // black text on white
          border: 'none',
          borderRadius: '16px'
        }
      },
      'membership-quiz': {
        'default': {
          backgroundColor: '#0000ff', // blue
          noticeColor: '#ffffff', // white text on blue
          buttonBg: '#ffffff', // white button
          buttonColor: '#0000ff', // blue text on white
          border: 'none',
          borderRadius: '8px'
        },
        'elegant': {
          backgroundColor: '#000080', // dark blue
          noticeColor: '#ffffff', // white text on dark blue
          buttonBg: '#ffffff', // white button
          buttonColor: '#000080', // dark blue text on white
          border: '2px solid #4169e1',
          borderRadius: '12px'
        },
        'soft': {
          backgroundColor: '#e6f3ff', // light blue
          noticeColor: '#0000ff', // blue text on light blue
          buttonBg: '#0000ff', // blue button
          buttonColor: '#ffffff', // white text on blue
          border: '1px solid #b3d9ff',
          borderRadius: '8px'
        },
        'corporate': {
          backgroundColor: '#ffffff', // white
          noticeColor: '#101022', // black text on white
          buttonBg: '#0000ff', // blue button
          buttonColor: '#ffffff', // white text on blue
          border: '2px solid #e0e0e0',
          borderRadius: '6px'
        }
      },
      'media-buzz': {
        'default': {
          backgroundColor: '#ffffff', // white
          noticeColor: '#101022', // black text on white
          buttonBg: '#ffff00', // yellow button
          buttonColor: '#101022', // black text on yellow
          border: '2px dashed #101022',
          borderRadius: '8px'
        },
        'news': {
          backgroundColor: '#f8f9fa', // light gray (close to white)
          noticeColor: '#101022', // black text
          buttonBg: '#0000ff', // blue button
          buttonColor: '#ffffff', // white text on blue
          border: '1px solid #e0e0e0',
          borderRadius: '8px'
        },
        'viral': {
          backgroundColor: '#ffff00', // yellow
          noticeColor: '#101022', // black text on yellow
          buttonBg: '#101022', // black button
          buttonColor: '#ffffff', // white text on black
          border: '2px solid #ffd700',
          borderRadius: '12px'
        },
        'trending': {
          backgroundColor: '#e6f3ff', // light blue
          noticeColor: '#0000ff', // blue text on light blue
          buttonBg: '#0000ff', // blue button
          buttonColor: '#ffffff', // white text on blue
          border: '1px solid #b3d9ff',
          borderRadius: '10px'
        }
      }
    };

    return styleVariants[variant]?.[styleVariant] || styleVariants[variant]?.['default'] || {
      backgroundColor: '#ff69b4',
      noticeColor: '#101022',
      buttonBg: '#101022',
      buttonColor: '#ffffff',
      border: 'none',
      borderRadius: '8px'
    };
  };

  const variantStyles = getVariantStyles(element.variant, element.styleVariant);

  // Debug: Log the fontWeight being applied
  console.log('🎨 Notice fontWeight:', element.subElementStyles?.notice?.fontWeight || 'bold');

  return (
    <Box
      data-element-id={element.id}
      sx={{
        position: 'relative',
        '&:hover .element-controls': { opacity: 1 },
        mb: 2,
        ...getSelectionStyles(element.id)
      }}
    >
      <ElementControls />
      <Box
        sx={{
          background: variantStyles.backgroundColor.includes('gradient')
            ? variantStyles.backgroundColor
            : variantStyles.backgroundColor,
          padding: '20px',
          borderRadius: variantStyles.borderRadius,
          textAlign: 'center',
          border: variantStyles.border,
          boxShadow: element.styleVariant === 'gradient' ? '0 8px 32px rgba(255, 107, 107, 0.3)' : '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        {/* Notice Text */}
        {isEditingField('content.notice') ? (
          <TextField
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onKeyDown={handleKeyPress}
            onBlur={saveInlineEdit}
            autoFocus
            multiline
            rows={3}
            variant="outlined"
            size="small"
            fullWidth
            sx={{ mb: 2 }}
          />
        ) : (
          <Typography
            variant="body1"
            sx={{
              // Apply custom sub-element styles if available, otherwise use variant styles
              color: element.subElementStyles?.notice?.color || variantStyles.noticeColor,
              backgroundColor: element.subElementStyles?.notice?.backgroundColor || 'transparent',
              fontSize: element.subElementStyles?.notice?.fontSize || 'inherit',
              fontFamily: element.subElementStyles?.notice?.fontFamily || 'inherit',
              fontWeight: element.subElementStyles?.notice?.fontWeight || 'bold',
              textAlign: element.subElementStyles?.notice?.textAlign || 'inherit',
              border: element.subElementStyles?.notice?.border || 'none',
              borderRadius: element.subElementStyles?.notice?.borderRadius || '0px',
              padding: element.subElementStyles?.notice?.padding || '8px',
              margin: element.subElementStyles?.notice?.margin || '0px',
              mb: 2,
              cursor: 'pointer',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
              // Selection styling - use boxShadow for visual feedback, don't override custom border
              ...(selectedSubElement === 'notice' && {
                boxShadow: '0 0 0 2px #4caf50, 0 0 0 4px rgba(76, 175, 80, 0.2)'
              })
            }}
            onClick={(e) => {
              e.stopPropagation();
              console.log('🟢 Notice text clicked - selecting sub-element: notice');
              setSelectedElement(element);
              setSelectedElementContext({ isInZone, layoutId, zoneId });
              setSelectedSubElement('notice');
              setTabValue(1); // Set to Style tab
              setRightPanelOpen(true);
            }}
            onDoubleClick={() => handleDoubleClick('content.notice', element.content?.notice)}
            title="Click to select for styling • Double-click to edit notice text"
          >
            {element.content?.notice?.split('\n').map((line, index) => (
              <span key={index}>
                {line.includes('70%') ? (
                  <span style={{
                    color: element.subElementStyles?.notice?.color || variantStyles.noticeColor,
                    fontWeight: element.subElementStyles?.notice?.fontWeight || 'bold'
                  }}>{line}</span>
                ) : line.includes('exclusive link below') ? (
                  <span>
                    {line.replace('exclusive link below', '')}
                    <span style={{ color: '#1976d2', textDecoration: 'underline' }}>exclusive link below</span>
                  </span>
                ) : (
                  line
                )}
                {index < element.content?.notice?.split('\n').length - 1 && <br />}
              </span>
            ))}
          </Typography>
        )}

        {/* Call to Action Text */}
        {isEditingField('content.callToAction') ? (
          <TextField
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onKeyDown={handleKeyPress}
            onBlur={saveInlineEdit}
            autoFocus
            variant="outlined"
            size="small"
            fullWidth
            sx={{ mb: 2 }}
          />
        ) : (
          <Typography
            variant="body1"
            sx={{
              // Apply custom sub-element styles if available, otherwise use variant styles
              color: element.subElementStyles?.callToAction?.color || variantStyles.noticeColor,
              backgroundColor: element.subElementStyles?.callToAction?.backgroundColor || 'transparent',
              fontSize: element.subElementStyles?.callToAction?.fontSize || 'inherit',
              fontFamily: element.subElementStyles?.callToAction?.fontFamily || 'inherit',
              fontWeight: element.subElementStyles?.callToAction?.fontWeight || 'normal',
              textAlign: element.subElementStyles?.callToAction?.textAlign || 'inherit',
              border: element.subElementStyles?.callToAction?.border || 'none',
              borderRadius: element.subElementStyles?.callToAction?.borderRadius || '0px',
              padding: element.subElementStyles?.callToAction?.padding || '8px',
              margin: element.subElementStyles?.callToAction?.margin || '0px',
              mb: 2,
              cursor: 'pointer',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
              // Selection styling - use boxShadow for visual feedback, don't override custom border
              ...(selectedSubElement === 'callToAction' && {
                boxShadow: '0 0 0 2px #4caf50, 0 0 0 4px rgba(76, 175, 80, 0.2)'
              })
            }}
            onClick={(e) => {
              e.stopPropagation();
              console.log('🟡 Call-to-action text clicked - selecting sub-element: callToAction');
              setSelectedElement(element);
              setSelectedElementContext({ isInZone, layoutId, zoneId });
              setSelectedSubElement('callToAction');
              setTabValue(1); // Set to Style tab
              setRightPanelOpen(true);
            }}
            onDoubleClick={() => handleDoubleClick('content.callToAction', element.content?.callToAction)}
            title="Click to select for styling • Double-click to edit call to action text"
          >
            {element.content?.callToAction}
          </Typography>
        )}

        {/* Button */}
        {isEditingField('content.buttonText') ? (
          <TextField
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onKeyDown={handleKeyPress}
            onBlur={saveInlineEdit}
            autoFocus
            variant="outlined"
            size="small"
            fullWidth
            sx={{ mb: 1 }}
          />
        ) : (
          <ResizableButton
            element={element}
            updateElement={updateElement}
            isInZone={isInZone}
            layoutId={layoutId}
            zoneId={zoneId}
            isEditing={isEditingField('content.buttonText')}
            disableResize={true}
          >
            <button
              style={{
                // Apply custom sub-element styles if available, otherwise use variant styles
                backgroundColor: element.subElementStyles?.button?.backgroundColor || variantStyles.buttonBg,
                color: element.subElementStyles?.button?.color || variantStyles.buttonColor,
                fontSize: element.subElementStyles?.button?.fontSize || '18px',
                fontFamily: element.subElementStyles?.button?.fontFamily || 'inherit',
                fontWeight: element.subElementStyles?.button?.fontWeight || 'bold',
                textAlign: element.subElementStyles?.button?.textAlign || 'center',
                border: element.subElementStyles?.button?.border || 'none',
                borderRadius: element.subElementStyles?.button?.borderRadius || '8px',
                padding: element.subElementStyles?.button?.padding || '16px 32px',
                margin: element.subElementStyles?.button?.margin || '0px',
                cursor: 'pointer',
                width: '100%',
                height: '100%',
                maxWidth: '400px',
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                // Selection styling - use boxShadow for visual feedback, don't override custom border
                boxShadow: selectedSubElement === 'button' ? '0 0 0 2px #4caf50, 0 0 0 4px rgba(76, 175, 80, 0.2), 0 2px 4px rgba(0,0,0,0.1)' : '0 2px 4px rgba(0,0,0,0.1)',
                transition: 'all 0.2s ease'
              }}
              onClick={(e) => {
                e.stopPropagation();
                console.log('🔵 Button clicked - selecting sub-element: button');
                setSelectedElement(element);
                setSelectedElementContext({ isInZone, layoutId, zoneId });
                setSelectedSubElement('button');
                setTabValue(1); // Set to Style tab
                setRightPanelOpen(true); // Ensure right panel is open
              }}
              onDoubleClick={() => handleDoubleClick('content.buttonText', element.content?.buttonText)}
              title="Click to select for styling • Double-click to edit button text • Drag handles to resize"
            >
              {element.content?.buttonText}
            </button>
          </ResizableButton>
        )}


      </Box>
    </Box>
  );

case ELEMENT_TYPES.BUTTON_IMAGE_CALLOUT:
  const getImageCalloutVariantStyles = (variant) => {
    const v = element.variants?.[variant] || {};
    return {
      background: v.background || '#fff',
      textColor: v.textColor || '#222',
      border: v.border || 'none',
      buttonColor: v.buttonColor || '#1976d2',
      badgeColor: v.badgeColor || '#ffcc00',
      badge2Color: v.badge2Color || '#ffcc00',
    };
  };
  const imageCalloutStyles = getImageCalloutVariantStyles(element.variant);
  return (
    <Box
      data-element-id={element.id}
      sx={{
        background: imageCalloutStyles.background,
        color: imageCalloutStyles.textColor,
        border: imageCalloutStyles.border,
        borderRadius: '16px',
        mb: 2,
        textAlign: 'center',
        p: { xs: 2, md: 4 },
        boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
        position: 'relative',
        '&:hover .element-controls': { opacity: 1 },
        ...getSelectionStyles(element.id)
      }}
    >
      <ElementControls />
      {/* Headline */}
      {isEditingField('content.headline') ? (
        <TextField
          value={tempValue}
          onChange={(e) => setTempValue(e.target.value)}
          onKeyDown={handleKeyPress}
          onBlur={saveInlineEdit}
          autoFocus
          variant="outlined"
          size="small"
          fullWidth
          sx={{ mb: 2 }}
        />
      ) : (
        <Typography
          variant="h6"
          sx={{
            color: imageCalloutStyles.textColor,
            fontWeight: 'bold',
            mb: 2,
            cursor: 'pointer',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
            p: 1,
            borderRadius: 1
          }}
          onDoubleClick={() => handleDoubleClick('content.headline', element.content?.headline)}
          title="Double-click to edit headline"
        >
          {element.content?.headline}
        </Typography>
      )}

      {/* Image and Badges */}
      <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
        {isEditingField('content.image') ? (
          <TextField
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onKeyDown={handleKeyPress}
            onBlur={saveInlineEdit}
            autoFocus
            variant="outlined"
            size="small"
            fullWidth
            sx={{ mb: 2 }}
            label="Image URL"
          />
        ) : (
          <img
            src={element.content?.image}
            alt="callout"
            style={{
              maxWidth: '260px',
              width: '100%',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
              background: '#fff',
              cursor: 'pointer',
              marginBottom: 0
            }}
            onDoubleClick={() => handleDoubleClick('content.image', element.content?.image)}
            title="Double-click to edit image URL"
          />
        )}
        {/* Badge 1 */}
        {element.content?.badge && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              background: element.content?.badgeColor || imageCalloutStyles.badgeColor,
              color: '#222',
              fontWeight: 'bold',
              borderRadius: '50%',
              px: 2,
              py: 1,
              fontSize: '1rem',
              boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
            }}
          >
            {element.content?.badge}
          </Box>
        )}
        {/* Badge 2 (for wellamoon) */}
        {element.content?.badge2 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              background: element.content?.badge2Color || imageCalloutStyles.badge2Color,
              color: '#222',
              fontWeight: 'bold',
              borderRadius: '16px',
              px: 2,
              py: 1,
              fontSize: '0.95rem',
              boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
            }}
          >
            {element.content?.badge2}
          </Box>
        )}
      </Box>

      {/* Subheadline/Description */}
      {element.content?.subheadline && (
        isEditingField('content.subheadline') ? (
          <TextField
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onKeyDown={handleKeyPress}
            onBlur={saveInlineEdit}
            autoFocus
            variant="outlined"
            size="small"
            fullWidth
            sx={{ mb: 2 }}
          />
        ) : (
          <Typography
            variant="body1"
            sx={{
              color: imageCalloutStyles.textColor,
              mb: 2,
              cursor: 'pointer',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
              p: 1,
              borderRadius: 1
            }}
            onDoubleClick={() => handleDoubleClick('content.subheadline', element.content?.subheadline)}
            title="Double-click to edit subheadline"
          >
            {element.content?.subheadline?.split('\n').map((line, idx) => (
              <span key={idx}>{line}<br /></span>
            ))}
          </Typography>
        )
      )}

      {/* Button */}
      {isEditingField('content.buttonText') ? (
        <TextField
          value={tempValue}
          onChange={(e) => setTempValue(e.target.value)}
          onKeyDown={handleKeyPress}
          onBlur={saveInlineEdit}
          autoFocus
          variant="outlined"
          size="small"
          fullWidth
          sx={{ mb: 1 }}
        />
      ) : (
        <button
          style={{
            backgroundColor: element.content?.buttonColor || imageCalloutStyles.buttonColor,
            color: '#fff',
            padding: '18px 0',
            fontSize: '1.3rem',
            fontWeight: 'bold',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            width: '100%',
            margin: '16px 0',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            transition: 'all 0.2s ease'
          }}
          onClick={(e) => {
            e.stopPropagation();
            console.log('Image callout button clicked - selecting element:', element.id);
            setSelectedElement(element);
            setSelectedElementContext({ isInZone, layoutId, zoneId });
            setTabValue(1); // Set to Style tab
            setRightPanelOpen(true); // Ensure right panel is open
          }}
          onDoubleClick={() => handleDoubleClick('content.buttonText', element.content?.buttonText)}
          title="Click to select and edit styles • Double-click to edit text"
        >
          {element.content?.buttonText}
        </button>
      )}
      {/* Button URL editing */}
      {isEditingField('content.buttonUrl') ? (
        <TextField
          value={tempValue}
          onChange={(e) => setTempValue(e.target.value)}
          onKeyDown={handleKeyPress}
          onBlur={saveInlineEdit}
          autoFocus
          variant="outlined"
          size="small"
          fullWidth
          sx={{ mb: 1 }}
          label="Button URL"
        />
      ) : (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            cursor: 'pointer',
            color: '#888',
            mb: 1
          }}
          onDoubleClick={() => handleDoubleClick('content.buttonUrl', element.content?.buttonUrl)}
          title="Double-click to edit button URL"
        >
          {element.content?.buttonUrl ? `URL: ${element.content?.buttonUrl}` : 'Double-click to set button URL'}
        </Typography>
      )}

      {/* Subheadline Icon (e.g., lock) */}
      {element.content?.subheadlineIcon === 'lock' && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
          <LockIcon fontSize="small" sx={{ color: '#888', mr: 0.5 }} />
          <Typography variant="caption" color="#888">
            {element.content?.subheadline}
          </Typography>
        </Box>
      )}

      {/* Variant Selector */}
      <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
        {['alpha-brain-dark', 'proactiv-light', 'alpha-brain-horizontal', 'wellamoon-bordered'].map((variant) => (
          <Button
            key={variant}
            size="small"
            variant={selectedVariantKey === variant ? 'contained' : 'outlined'}
            onClick={() => {
              const variantContent = element.variants?.[variant] || element.content;
              const updatedElement = {
                ...element,
                variant,
                content: variantContent
              };
              updateElement(element.id, updatedElement);
              setSelectedVariantKey(variant);
            }}
            sx={{ fontSize: '0.75rem' }}
          >
            {variant.replace(/-/g, ' ')}
          </Button>
        ))}
      </Box>
    </Box>
  );

case ELEMENT_TYPES.BUTTON_TEXT_VARIANT:
  const getTextVariantStyles = (variant) => {
    switch (variant) {
      case 'red-shadow':
        return {
          buttonText: 'Push Me',
          buttonUrl: '#',
          buttonColor: '#f5003c',
          textColor: '#fff',
          fontWeight: 'bold',
          fontSize: '2rem',
          borderRadius: '20px',
          boxShadow: '0 6px 0 #a8002a',
          icon: '',
          iconPosition: 'none',
          padding: '24px 0',
          width: '260px',
          margin: '0 auto'
        };
      case 'orange-rounded':
        return {
          buttonText: 'Add To Cart',
          buttonUrl: '#',
          buttonColor: '#f53c00',
          textColor: '#fff',
          fontWeight: 'normal',
          fontSize: '2rem',
          borderRadius: '32px',
          boxShadow: 'none',
          icon: '',
          iconPosition: 'none',
          padding: '24px 0',
          width: '100%',
          margin: '0 auto'
        };
      case 'yellow-gradient':
        return {
          buttonText: 'Claim Your Supply Now',
          buttonUrl: '#',
          buttonColor: 'linear-gradient(180deg, #ffe066 0%, #ffb347 100%)',
          textColor: '#222',
          fontWeight: 'bold',
          fontSize: '2rem',
          borderRadius: '16px',
          boxShadow: 'none',
          icon: 'cart',
          iconPosition: 'left',
          iconColor: '#d4a000',
          padding: '20px 0',
          width: '100%',
          margin: '0 auto'
        };
      default:
        return {
          buttonText: 'Push Me',
          buttonUrl: '#',
          buttonColor: '#f5003c',
          textColor: '#fff',
          fontWeight: 'bold',
          fontSize: '2rem',
          borderRadius: '20px',
          boxShadow: '0 6px 0 #a8002a',
          icon: '',
          iconPosition: 'none',
          padding: '24px 0',
          width: '260px',
          margin: '0 auto'
        };
    }
  };

  const buttonTextVariantStyles = getTextVariantStyles(element.variant);

  return (
    <Box
      data-element-id={element.id}
      sx={{
        position: 'relative',
        '&:hover .element-controls': { opacity: 1 },
        mb: 2,
        ...getSelectionStyles(element.id)
      }}
    >
      <ElementControls />
      <Box
        sx={{
          backgroundColor: buttonTextVariantStyles.buttonColor,
          padding: '20px',
          borderRadius: '8px',
          textAlign: 'center',
          border: selectedVariantKey === 'media-buzz' ? '2px dashed #333' : 'none'
        }}
      >
        {/* Notice Text */}
        {isEditingField('content.notice') ? (
          <TextField
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onKeyDown={handleKeyPress}
            onBlur={saveInlineEdit}
            autoFocus
            multiline
            rows={3}
            variant="outlined"
            size="small"
            fullWidth
            sx={{ mb: 2 }}
          />
        ) : (
          <Typography
            variant="body1"
            sx={{
              color: buttonTextVariantStyles.noticeColor,
              fontWeight: 'bold',
              mb: 2,
              cursor: 'pointer',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
              p: 1,
              borderRadius: 1
            }}
            onDoubleClick={() => handleDoubleClick('content.notice', element.content?.notice)}
            title="Double-click to edit notice text"
          >
            {element.content?.notice?.split('\n').map((line, index) => (
              <span key={index}>
                {line.includes('70%') ? (
                  <span style={{ color: variantStyles.noticeColor, fontWeight: 'bold' }}>{line}</span>
                ) : line.includes('exclusive link below') ? (
                  <span>
                    {line.replace('exclusive link below', '')}
                    <span style={{ color: '#1976d2', textDecoration: 'underline' }}>exclusive link below</span>
                  </span>
                ) : (
                  line
                )}
                {index < element.content?.notice?.split('\n').length - 1 && <br />}
              </span>
            ))}
          </Typography>
        )}

        {/* Call to Action Text */}
        {isEditingField('content.callToAction') ? (
          <TextField
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onKeyDown={handleKeyPress}
            onBlur={saveInlineEdit}
            autoFocus
            variant="outlined"
            size="small"
            fullWidth
            sx={{ mb: 2 }}
          />
        ) : (
          <Typography
            variant="body1"
            sx={{
              color: buttonTextVariantStyles.noticeColor,
              mb: 2,
              cursor: 'pointer',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
              p: 1,
              borderRadius: 1
            }}
            onDoubleClick={() => handleDoubleClick('content.callToAction', element.content?.callToAction)}
            title="Double-click to edit call to action text"
          >
            {element.content?.callToAction}
          </Typography>
        )}

        {/* Button */}
        {isEditingField('content.buttonText') ? (
          <TextField
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onKeyDown={handleKeyPress}
            onBlur={saveInlineEdit}
            autoFocus
            variant="outlined"
            size="small"
            fullWidth
            sx={{ mb: 1 }}
          />
        ) : (
          <button
            style={{
              backgroundColor: buttonTextVariantStyles.buttonColor,
              color: buttonTextVariantStyles.textColor,
              padding: '16px 32px',
              fontSize: '18px',
              fontWeight: 'bold',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              width: '100%',
              maxWidth: '400px',
              marginBottom: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
            onClick={(e) => {
              e.stopPropagation();
              console.log('Button text variant clicked - selecting element:', element.id);
              setSelectedElement(element);
              setSelectedElementContext({ isInZone, layoutId, zoneId });
              setTabValue(1); // Set to Style tab
              setRightPanelOpen(true); // Ensure right panel is open
            }}
            onDoubleClick={() => handleDoubleClick('content.buttonText', element.content?.buttonText)}
            title="Click to select and edit styles • Double-click to edit text"
          >
            {element.content?.buttonText}
          </button>
        )}

        {/* Variant Selector */}
        <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
          {['red-shadow', 'orange-rounded', 'yellow-gradient'].map((variant) => (
            <Button
              key={variant}
              size="small"
              variant={selectedVariantKey === variant ? 'contained' : 'outlined'}
              onClick={() => {
                const variantContent = element.variants?.[variant] || element.content;
                const updatedElement = {
                  ...element,
                  variant,
                  content: variantContent
                };
                updateElement(element.id, updatedElement);
                setSelectedVariantKey(variant);
              }}
              sx={{ fontSize: '0.75rem' }}
            >
              {variant.replace('-', ' ')}
            </Button>
          ))}
        </Box>
      </Box>
    </Box>
  );

case ELEMENT_TYPES.BUTTON_HEADLINE_PRODUCT_IMAGE:
  return (
    <Box
      data-element-id={element.id}
      sx={{
        background: element.content?.background || '#ffffff',
        color: element.content?.textColor || '#333333',
        borderRadius: '16px',
        mb: 2,
        textAlign: 'center',
        p: { xs: 2, md: 4 },
        boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
        position: 'relative',
        '&:hover .element-controls': { opacity: 1 },
        ...getSelectionStyles(element.id)
      }}
    >
      <ElementControls />
      {/* Headline */}
      {isEditingField('content.headline') ? (
        <TextField
          value={tempValue}
          onChange={(e) => setTempValue(e.target.value)}
          onKeyDown={handleKeyPress}
          onBlur={saveInlineEdit}
          autoFocus
          variant="outlined"
          size="small"
          fullWidth
          sx={{ mb: 2 }}
        />
      ) : (
        <Typography
          variant="h6"
          sx={{
            color: element.content?.textColor || '#333333',
            fontWeight: 'bold',
            mb: 2,
            cursor: 'pointer',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
            p: 1,
            borderRadius: 1
          }}
          onDoubleClick={() => handleDoubleClick('content.headline', element.content?.headline)}
          title="Double-click to edit headline"
        >
          {element.content?.headline}
        </Typography>
      )}

      {/* Product Image */}
      <Box sx={{ mb: 2 }}>
        {isEditingField('content.productImage') ? (
          <TextField
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onKeyDown={handleKeyPress}
            onBlur={saveInlineEdit}
            autoFocus
            variant="outlined"
            size="small"
            fullWidth
            sx={{ mb: 1 }}
            label="Image URL"
          />
        ) : (
          <Box
            sx={{
              cursor: 'pointer',
              '&:hover': { opacity: 0.8 },
              mb: 1
            }}
            onDoubleClick={() => handleDoubleClick('content.productImage', element.content?.productImage)}
            title="Double-click to edit image URL"
          >
            <img
              src={element.content?.productImage || '/api/placeholder/400/300'}
              alt="Product"
              style={{
                width: '100%',
                maxWidth: '400px',
                height: 'auto',
                borderRadius: '8px',
                border: '2px dashed #e0e0e0'
              }}
            />
          </Box>
        )}
      </Box>

      {/* Subheadline */}
      {element.content?.subheadline && (
        isEditingField('content.subheadline') ? (
          <TextField
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onKeyDown={handleKeyPress}
            onBlur={saveInlineEdit}
            autoFocus
            multiline
            rows={2}
            variant="outlined"
            size="small"
            fullWidth
            sx={{ mb: 2 }}
          />
        ) : (
          <Typography
            variant="body1"
            sx={{
              color: element.content?.textColor || '#333333',
              mb: 2,
              cursor: 'pointer',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
              p: 1,
              borderRadius: 1
            }}
            onDoubleClick={() => handleDoubleClick('content.subheadline', element.content?.subheadline)}
            title="Double-click to edit subheadline"
          >
            {element.content?.subheadline?.split('\n').map((line, idx) => (
              <span key={idx}>{line}<br /></span>
            ))}
          </Typography>
        )
      )}

      {/* Button */}
      {isEditingField('content.buttonText') ? (
        <TextField
          value={tempValue}
          onChange={(e) => setTempValue(e.target.value)}
          onKeyDown={handleKeyPress}
          onBlur={saveInlineEdit}
          autoFocus
          variant="outlined"
          size="small"
          fullWidth
          sx={{ mb: 1 }}
        />
      ) : (
        <button
          style={{
            backgroundColor: element.content?.buttonColor || '#4caf50',
            color: '#fff',
            padding: '18px 0',
            fontSize: '1.3rem',
            fontWeight: 'bold',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            width: '100%',
            margin: '16px 0',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            transition: 'all 0.2s ease'
          }}
          onClick={(e) => {
            e.stopPropagation();
            console.log('Button headline product image clicked - selecting element:', element.id);
            setSelectedElement(element);
            setSelectedElementContext({ isInZone, layoutId, zoneId });
            setTabValue(1); // Set to Style tab
            setRightPanelOpen(true); // Ensure right panel is open
          }}
          onDoubleClick={() => handleDoubleClick('content.buttonText', element.content?.buttonText)}
          title="Click to select and edit styles • Double-click to edit text"
        >
          {element.content?.buttonText}
        </button>
      )}

      {/* Button URL editing */}
      {isEditingField('content.buttonUrl') ? (
        <TextField
          value={tempValue}
          onChange={(e) => setTempValue(e.target.value)}
          onKeyDown={handleKeyPress}
          onBlur={saveInlineEdit}
          autoFocus
          variant="outlined"
          size="small"
          fullWidth
          sx={{ mb: 1 }}
          label="Button URL"
        />
      ) : (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            cursor: 'pointer',
            color: '#888',
            mb: 1
          }}
          onDoubleClick={() => handleDoubleClick('content.buttonUrl', element.content?.buttonUrl)}
          title="Double-click to edit button URL"
        >
          {element.content?.buttonUrl ? `URL: ${element.content?.buttonUrl}` : 'Double-click to set button URL'}
        </Typography>
      )}
    </Box>
  );
    default:
      return null;
  }
};
